import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { TableIcon, ChartNoAxesCombined } from 'lucide-react';
import Filters from '../Filters/Filters';
import TableHeader from '../TableControls/TableHeader';
import ReportCards from '../ReportCards/ReportCards';
import LocationTypeTotalsTable from '../Tables/LocationTypeTotalsTable';
import LocationTypeTrendsTable from '../Tables/LocationTypeTrendsTable';
import LocationTypeRatesTable from '../Tables/LocationTypeRatesTable';
import LocationTypeScoresTable from '../Tables/LocationTypeScoresTable';
import LocationNameTotalsTable from '../Tables/LocationNameTotalsTable';
import LocationNameTrendsTable from '../Tables/LocationNameTrendsTable';
import LocationNameRatesTable from '../Tables/LocationNameRatesTable';
import LocationNameScoresTable from '../Tables/LocationNameScoresTable';
import LocationNameTotalsChart from '../Charts/LocationNameTotalsChart';
import LocationNameTrendsChart from '../Charts/LocationNameTrendsChart';
import LocationNameRatesChart from '../Charts/LocationNameRatesChart';
import LocationNameScoresChart from '../Charts/LocationNameScoresChart';
import HistoricalTotalsTable from '../Tables/HistoricalTotalsTable';
import HistoricalTrendsTable from '../Tables/HistoricalTrendsTable';
import HistoricalRatesTable from '../Tables/HistoricalRatesTable';
import HistoricalScoresTable from '../Tables/HistoricalScoresTable';
import HistoricalTotalsChart from '../Charts/HistoricalTotalsChart';
import HistoricalTrendsChart from '../Charts/HistoricalTrendsChart';
import HistoricalRatesChart from '../Charts/HistoricalRatesChart';
import HistoricalScoresChart from '../Charts/HistoricalScoresChart';
import Breadcrumb from '../TableControls/Breadcrumb';
import Pagination from '../TableControls/Pagination';
import LoadingOverlay from '../Loading/LoadingOverlay';
import { fetchLocationData, fetchLocationNameHistoricalData, checkLocationYears } from '../../services/api';
import {
  getStateAbbreviation,
  extractState,
  isStateScopedCityCountyAggregate,
} from '../../constants/locations';
import './DataView.css';

const DataView = ({ 
  selectedView, 
  setSelectedView, 
  activeTab, 
  setActiveTab,
  isHistorical,
  setIsHistorical,
  isPinned,
  setIsPinned,
  isMobile,
  selectedLocation,
  onLocationSelect,
  displayedLocation,
  setDisplayedLocation,
  selectedCrime,
  setSelectedCrime,
  selectedDateRange,
  setSelectedDateRange,
  selectedSource,
  setSelectedSource,
  compareMode,
  onCompareModeChange,
  rateViewMode,
  setRateViewMode,
  getActiveCompareMode,
  selectedChartOption,
  onChartOptionChange,
  onMapRangeChange,
  sortConfig,
  onSortChange,
  hoveredLocation,
  setHoveredLocation,
  setAvailableYears,
  availableYears,
  showCityResolutionModal,
  setShowCityResolutionModal,
  setCityResolutionData,
  yearCheckInProgress,
  selectedData,
  setSelectedData,
  onSaveSearch,
  userPlan,
  mostRecentYear,
  onRequestUpgrade,
  captureRef,
  onExportContextChange,
  populationGroup,
  setPopulationGroup,
  popGroupSetByRouterRef,
  currentPage,
  setCurrentPage,
  itemsPerPage,
  setItemsPerPage,
  onResolvedCityDisplayName,
}) => {
  const [locationType, setLocationType] = useState(null);
  const [historicalData, setHistoricalData] = useState(null);
  const [historicalDataSource, setHistoricalDataSource] = useState(selectedSource);
  const [view, setView] = useState('locationName');
  const [showLoading, setShowLoading] = useState(false);
  const [isHistoricalLoading, setIsHistoricalLoading] = useState(false);
  const [hasResolvedHistoricalFetch, setHasResolvedHistoricalFetch] = useState(false);
  const [isStateTransition, setIsStateTransition] = useState(false);  
  const [ratesTableFilter, setRatesTableFilter] = useState(null);
  const [totalItems, setTotalItems] = useState(0);

  const [locationData, setLocationData] = useState({
    info: null,
    data: null,
    loading: false,
    error: null,
    dateRange: selectedDateRange,
    source: selectedSource
  });

  const [locationTypeData, setLocationTypeData] = useState({
    data: null,
    type: null,
    dateRange: null,
    crime: null,
    source: selectedSource,
    loading: false,
    error: null
  });

  const isFetching = useRef(false);
  const fetchId = useRef(0);
  const lastFetchParamsRef = useRef(null);
  const pendingPopulationReset = useRef(false);
  const lastLocationKeyRef = useRef(null);
  const contentRef = useRef(null);

  const startItem = ((currentPage - 1) * itemsPerPage) + 1;
  const endItem = Math.min(startItem + itemsPerPage - 1, totalItems);
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const isNation = displayedLocation?.info?.type?.toLowerCase() === 'nation';

  const fetchData = useCallback(async (locationInfo, dateRange, crime, source, skipResolution = false, thisFetchId) => {
    setLocationData(prev => ({ ...prev, loading: true }));
    setLocationTypeData(prev => ({ ...prev, loading: true }));
    setShowLoading(true);

    try {
      const { name, type } = locationInfo;
      const isAllCounties = type === 'county' && name === 'All Counties';
      const isAllCities = type === 'city' && name === 'All Cities';

      const includeGeometry = !isAllCounties && !isAllCities;
      const locationResult = await fetchLocationData(name, dateRange, type, source, includeGeometry, crime, skipResolution);

      if (thisFetchId !== fetchId.current) {
        return;
      }

      if (locationResult.requiresCityResolution && !skipResolution) {
        setCityResolutionData(locationResult.cityResolutionData);
        setShowCityResolutionModal(true);
        setShowLoading(false);
        setLocationData(prev => ({ ...prev, loading: false }));
        setLocationTypeData(prev => ({ ...prev, loading: false }));
        return;
      }

      const { typeData, locationData: locData } = locationResult;

      let finalLocationInfo = locationInfo;
      if (locData.wasResolved && locData.resolvedName) {
        finalLocationInfo = {
          ...locationInfo,
          name: locData.resolvedName,
          originalSearch: name,
          wasResolved: true
        };
        if (
          type === 'city' &&
          name.trim().toLowerCase() !== locData.resolvedName.trim().toLowerCase()
        ) {
          onResolvedCityDisplayName?.({
            from: name,
            to: locData.resolvedName,
            type,
          });
        }
      }

      let currentLocationData = locData.current;
      if ((!currentLocationData || Object.keys(currentLocationData).length === 0) && 
          locData.wasResolved && 
          locData.resolvedName && 
          typeData?.locations) {
        
        const [cityName, stateName] = locData.resolvedName.split(',').map(s => s.trim());
        
        currentLocationData = typeData.locations.find(loc => 
          loc.city?.toLowerCase() === cityName.toLowerCase() &&
          loc.state?.toLowerCase() === stateName.toLowerCase()
        );
      }

      if (typeData?.availableYears && Array.isArray(typeData.availableYears)) {
        if (setAvailableYears && locationInfo.name.startsWith('All ')) {
          setAvailableYears(typeData.availableYears);
        }
      }

      if (typeData?.locations) {
        const totalItems = typeData.locations.length || 0;
        setItemsPerPage(totalItems <= 20 && ![5, 10, 15].includes(totalItems) ? totalItems : 20);
        setTotalItems(totalItems);
        setCurrentPage(1);
      }

      if ((type === 'city' || type === 'county') && typeData?.locations?.[0]) {
        const isAllNational = locationInfo.name === 'All Counties' || locationInfo.name === 'All Cities';
        const isAllState = locationInfo.name?.startsWith('All ') &&
          locationInfo.name?.includes(type === 'county' ? 'Counties' : 'Cities') &&
          !isAllNational;

        const totalInState = type === 'city'
          ? typeData.locations[0].total_cities_state
          : typeData.locations[0].total_counties_state;

        if (totalInState === 1) {
          onCompareModeChange('national');
          setRateViewMode({
            rateType: type,
            rateScope: 'national'
          });
        } else {
          if (isAllNational) {
            onCompareModeChange('national');
            setRateViewMode((prev) => {
              const aggregate =
                type === 'county' ? 'all_counties' : 'all_cities';
              if (prev.rateType === aggregate) {
                return { rateType: aggregate, rateScope: 'national' };
              }
              return { rateType: type, rateScope: 'national' };
            });
          } else if (isAllState) {
            setRateViewMode((prev) => {
              const peer = type === 'county' ? 'county' : 'city';
              const aggregate = type === 'county' ? 'all_counties' : 'all_cities';
              const keepType =
                prev.rateType === peer || prev.rateType === aggregate;
              return {
                rateType: keepType ? prev.rateType : aggregate,
                rateScope: compareMode,
              };
            });
          } else if (!locationInfo.name.startsWith('All ')) {
            const scope = compareMode === 'national' ? 'national' : 'state';
            const peer = type === 'county' ? 'county' : 'city';
            const aggregate = type === 'county' ? 'all_counties' : 'all_cities';
            setRateViewMode((prev) => {
              if (prev.rateType === aggregate) {
                return { rateType: prev.rateType, rateScope: scope };
              }
              return { rateType: peer, rateScope: scope };
            });
          }
        }
      }

      setLocationTypeData({
        data: typeData,
        type,
        dateRange,
        crime,
        source,
        loading: false,
        error: null,
      });

      setLocationData({
        info: finalLocationInfo,
        data: { current: currentLocationData || typeData?.aggregateStats },
        loading: false,
        error: null,
        dateRange,
        source,
      });

      setDisplayedLocation({
        info: finalLocationInfo,
        data: { current: currentLocationData || typeData?.aggregateStats },
      });

      if (locData.wasResolved) {
        if (typeData?.availableYears && !typeData.availableYears.includes(parseInt(dateRange))) {
          const mostRecentYear = Math.max(...typeData.availableYears).toString();
          
          setSelectedDateRange(mostRecentYear);
          return;
        }
        
        setDisplayedLocation({
          info: finalLocationInfo,
          data: { current: currentLocationData || typeData?.aggregateStats },
          loading: false,
          error: null
        });
        
        onLocationSelect({
          name: locData.resolvedName,
          type: locationInfo.type,
          info: {
            ...finalLocationInfo,
            skipResolutionCheck: true,
            skipYearCheck: true,
            isRecursiveCall: true,
          }
        });
      }

      setShowLoading(false);
    } catch (error) {
      if (thisFetchId !== fetchId.current) {
        return;
      }

      console.error('Error fetching data:', error);
      setLocationTypeData(prev => ({ ...prev, loading: false, error: error.message }));
      setLocationData(prev => ({ ...prev, loading: false, error: error.message }));
      setShowLoading(false);
    }
  }, [
    setSelectedDateRange,
    setAvailableYears, 
    setDisplayedLocation, 
    onLocationSelect,
    onResolvedCityDisplayName,
    setCityResolutionData, 
    setShowCityResolutionModal,
    compareMode,
    onCompareModeChange,
    setRateViewMode,
    setCurrentPage,
    setItemsPerPage,
  ]);

  // 1. Core data fetching effect - handles all main data loading
  useEffect(() => {
    if (!selectedLocation?.info?.name || !selectedLocation?.info?.type) return;

    const { name: newName, type: newType } = selectedLocation.info;
    const { name: prevName, type: prevType } = locationData.info || {};

    const newState = extractState(selectedLocation.info);
    const prevState = extractState(locationData.info);

    const hasLocationChanged = newName !== prevName || newType !== prevType;
    const hasStateChanged = newState !== prevState;
    const hasFiltersChanged = 
      selectedDateRange !== locationData.dateRange ||
      selectedSource !== locationData.source ||
      ((newType === 'city' || newType === 'county') && newName.startsWith('All ') && selectedCrime !== locationTypeData.crime);
      
    if (!hasLocationChanged && !hasFiltersChanged && !hasStateChanged) {
      const currentParams = `${newName}-${newType}-${selectedDateRange}-${selectedCrime}-${selectedSource}`;
      if (!isFetching.current || lastFetchParamsRef.current === currentParams) {
        setShowLoading(false);
        return;
      }
    }

    const isAllCountiesInState = newType === 'county' && newName.startsWith('All ') && newName.endsWith(' Counties');
    const isAllCitiesInState = newType === 'city' && newName.startsWith('All ') && newName.endsWith(' Cities');
    const prevWasAll = prevName && prevName.startsWith('All ');
    const isBothAllStateViews = prevWasAll && prevState && newState && (isAllCitiesInState || isAllCountiesInState);
    const isAllNational = newName === 'All Counties' || newName === 'All Cities';
    const isAllStateTransition = 
      isBothAllStateViews && !isAllNational &&
      newType === prevType &&
      selectedDateRange === locationData.dateRange &&
      selectedSource === locationData.source &&
      ((newType === 'city' || newType === 'county') && selectedCrime === locationTypeData.crime);

    const prevAgg = prevName && prevType
      ? isStateScopedCityCountyAggregate({ info: { name: prevName, type: prevType } })
      : false;
    const newAgg = isStateScopedCityCountyAggregate({ info: { name: newName, type: newType } });
    const isAggregatePeerSwap =
      hasLocationChanged &&
      newType === prevType &&
      (newType === 'city' || newType === 'county') &&
      newState &&
      prevState &&
      newState === prevState &&
      ((prevAgg && !newName.startsWith('All ')) ||
        (newAgg && prevName && !prevName.startsWith('All ')));

    setIsStateTransition(isAllStateTransition || isAggregatePeerSwap);

    const currentParams = `${newName}-${newType}-${selectedDateRange}-${selectedCrime}-${selectedSource}`;
    
    if (isFetching.current && lastFetchParamsRef.current === currentParams) return;

    const currentFetchId = ++fetchId.current;
    
    lastFetchParamsRef.current = currentParams;

    if (hasLocationChanged && yearCheckInProgress) {
      yearCheckInProgress.current = true;
    }

    if (hasFiltersChanged && newName.startsWith('All ')) {
      const currentFetchId = ++fetchId.current;

      isFetching.current = true;
      fetchData(selectedLocation.info, selectedDateRange, selectedCrime, selectedSource, false, currentFetchId)
        .finally(() => {
          if (currentFetchId === fetchId.current) {
            isFetching.current = false;
          }
        });
      return;
    }

    // Scenario 1: Specific to Specific within same type and state
    if (
      hasLocationChanged &&
      newType === prevType &&
      newState === prevState &&
      !newName.startsWith('All ') &&
      prevName && !prevName.startsWith('All ') &&
      locationTypeData.data &&
      selectedDateRange === locationTypeData.dateRange
    ) {
      if (newType === 'city' || newType === 'county') {
        const shouldSkipResolution = selectedLocation.info?.skipResolutionCheck === true;
        isFetching.current = false;

        fetchLocationData(
          newName,
          selectedDateRange,
          newType,
          selectedSource,
          false, 
          selectedCrime,
          shouldSkipResolution,
          true
        )
          .then(result => {
            if (result.requiresCityResolution) {
              setCityResolutionData(result.cityResolutionData);
              setShowCityResolutionModal(true);
              setLocationTypeData(prev => ({ ...prev, loading: false }));
              setLocationData(prev => ({ ...prev, loading: false }));
              return;
            }
            
            const { locationData: locData } = result;
            
            if (locData.availableYears && Array.isArray(locData.availableYears)) {
              const yearsAsStrings = locData.availableYears.map(String);
              const currentYearString = String(selectedDateRange);
              
              if (!yearsAsStrings.includes(currentYearString)) {
                const mostRecent = Math.max(...locData.availableYears.map(y => parseInt(y))).toString();
                setAvailableYears(yearsAsStrings);
                setSelectedDateRange(mostRecent);
                setLocationTypeData(prev => ({ ...prev, loading: false }));
                return;
              }
              
              setAvailableYears(yearsAsStrings);
            }

            let finalInfo = selectedLocation.info;
            if (locData.wasResolved && locData.resolvedName) {
              finalInfo = {
                ...selectedLocation.info,
                name: locData.resolvedName,
                originalSearch: selectedLocation.info.name,
                wasResolved: true
              };
            }
            
            setLocationData({
              info: finalInfo,
              data: { current: locData.current },
              loading: false,
              error: null,
              dateRange: selectedDateRange,
              source: selectedSource,
            });
            setLocationTypeData(prev => ({ ...prev, loading: false }));
            
            setDisplayedLocation({
              info: finalInfo,
              data: { current: locData.current },
            });

            if (locData.wasResolved && locData.resolvedName) {
              onLocationSelect({
                name: locData.resolvedName,
                type: newType,
                info: {
                  ...finalInfo,
                  skipResolutionCheck: true,
                  skipYearCheck: true
                }
              });
            }
          })
          .catch(error => {
            console.error('Error fetching specific location:', error);
            setLocationData(prev => ({ ...prev, loading: false, error: error.message }));
            setLocationTypeData(prev => ({ ...prev, loading: false, error: error.message }));
          });
        
        if (yearCheckInProgress) {
          yearCheckInProgress.current = false;
        }
        
        return;
      }
      
      const cachedData = findCachedData(newName, newType, locationTypeData.data);
      if (cachedData) {
        isFetching.current = false;
        setLocationData({
          info: selectedLocation.info,
          data: { current: cachedData },
          loading: false,
          error: null,
          dateRange: selectedDateRange,
          source: selectedSource,
        });
        setLocationTypeData(prev => ({ ...prev, loading: false }));
        setDisplayedLocation({
          ...selectedLocation,
          data: { current: cachedData },
        });
        
        if (yearCheckInProgress) {
          yearCheckInProgress.current = false;
        }
        
        const { name, type } = selectedLocation.info;
        checkLocationYears(name, type, selectedSource)
          .then(years => {
            if (Array.isArray(years)) {
              setAvailableYears(years);
            } else if (years.availableYears && Array.isArray(years.availableYears)) {
              setAvailableYears(years.availableYears);
            }
          })
          .catch(error => console.error("Error fetching years:", error));
        
        return;
      }
    }

    // Scenario 2: Specific to All within same type and state
    if (
      hasLocationChanged &&
      newType === prevType &&
      newState === prevState &&
      newName.startsWith('All ') &&
      prevName && !prevName.startsWith('All ') &&
      locationTypeData.data?.aggregateStats
    ) {
      isFetching.current = false;
      if (locationTypeData.data?.locations?.[0]) {
        const totalInState = newType === 'city' 
          ? locationTypeData.data.locations[0].total_cities_state 
          : locationTypeData.data.locations[0].total_counties_state;
          
        if (totalInState === 1 && compareMode !== 'national') {
          onCompareModeChange('national');
        }
      }

      setLocationData({
        info: selectedLocation.info,
        data: { current: locationTypeData.data.aggregateStats },
        loading: false,
        error: null,
        dateRange: selectedDateRange,
        source: selectedSource,
      });
      setLocationTypeData(prev => ({ ...prev, loading: false }));
      setDisplayedLocation({
        ...selectedLocation,
        data: { current: locationTypeData.data.aggregateStats },
      });

      if (yearCheckInProgress) {
        yearCheckInProgress.current = false;
      }

      const { name, type } = selectedLocation.info;
        checkLocationYears(name, type, selectedSource)
          .then(years => {
            if (Array.isArray(years)) {
              setAvailableYears(years);
            } else if (years.availableYears && Array.isArray(years.availableYears)) {
              setAvailableYears(years.availableYears);
            }
          })
          .catch(error => console.error("Error fetching years:", error));

      return;
    }

    // Scenario 3: All to Specific within same type and state
    if (
      hasLocationChanged &&
      newType === prevType &&
      newState === prevState &&
      !newName.startsWith('All ') &&
      prevName?.startsWith('All ') &&
      locationTypeData.data
    ) {
      if (newType === 'city' || newType === 'county') {
        const shouldSkipResolution = selectedLocation.info?.skipResolutionCheck === true;
        isFetching.current = false;

        fetchLocationData(
          newName,
          selectedDateRange,
          newType,
          selectedSource,
          false, 
          selectedCrime,
          shouldSkipResolution,
          true 
        )
          .then(result => {
            if (result.requiresCityResolution) {
              setCityResolutionData(result.cityResolutionData);
              setShowCityResolutionModal(true);
              setLocationTypeData(prev => ({ ...prev, loading: false }));
              setLocationData(prev => ({ ...prev, loading: false }));
              return;
            }
            
            const { locationData: locData } = result;
            
            if (locData.availableYears && Array.isArray(locData.availableYears)) {
              const yearsAsStrings = locData.availableYears.map(String);
              const currentYearString = String(selectedDateRange);
              
              if (!yearsAsStrings.includes(currentYearString)) {
                const mostRecent = Math.max(...locData.availableYears.map(y => parseInt(y))).toString();
                setAvailableYears(yearsAsStrings);
                setSelectedDateRange(mostRecent);
                setLocationTypeData(prev => ({ ...prev, loading: false }));
                return;
              }
              
              setAvailableYears(yearsAsStrings);
            }

            let finalInfo = selectedLocation.info;
            if (locData.wasResolved && locData.resolvedName) {
              finalInfo = {
                ...selectedLocation.info,
                name: locData.resolvedName,
                originalSearch: selectedLocation.info.name,
                wasResolved: true
              };
            }
            
            setLocationData({
              info: finalInfo,
              data: { current: locData.current },
              loading: false,
              error: null,
              dateRange: selectedDateRange,
              source: selectedSource,
            });
            setLocationTypeData(prev => ({ ...prev, loading: false }));
            
            setDisplayedLocation({
              info: finalInfo,
              data: { current: locData.current },
            });

            if (locData.wasResolved && locData.resolvedName) {
              onLocationSelect({
                name: locData.resolvedName,
                type: newType,
                info: {
                  ...finalInfo,
                  skipResolutionCheck: true,
                  skipYearCheck: true
                }
              });
            }
          })
          .catch(error => {
            console.error('Error fetching specific location:', error);
            setLocationData(prev => ({ ...prev, loading: false, error: error.message }));
            setLocationTypeData(prev => ({ ...prev, loading: false, error: error.message }));
          });
        
        if (yearCheckInProgress) {
          yearCheckInProgress.current = false;
        }
        
        return;
      }
      
      const cachedData = findCachedData(newName, newType, locationTypeData.data);
      if (cachedData) {
        isFetching.current = false;
        setLocationData({
          info: selectedLocation.info,
          data: { current: cachedData },
          loading: false,
          error: null,
          dateRange: selectedDateRange,
          source: selectedSource,
        });
        setLocationTypeData(prev => ({ ...prev, loading: false }));
        setDisplayedLocation({
          ...selectedLocation,
          data: { current: cachedData },
        });
    
        if (yearCheckInProgress) {
          yearCheckInProgress.current = false;
        }
    
        const { name, type } = selectedLocation.info;
        checkLocationYears(name, type, selectedSource)
          .then(years => {
            if (Array.isArray(years)) {
              setAvailableYears(years);
            } else if (years.availableYears && Array.isArray(years.availableYears)) {
              setAvailableYears(years.availableYears);
            }
          })
          .catch(error => console.error("Error fetching years:", error));
          
        return;
      }
    }

    isFetching.current = true;
    const shouldSkipResolution = selectedLocation.info?.skipResolutionCheck === true;
    fetchData(selectedLocation.info, selectedDateRange, selectedCrime, selectedSource, shouldSkipResolution, currentFetchId)
      .finally(() => {
        if (currentFetchId === fetchId.current) {
          isFetching.current = false;
        }
      });
  }, [
    selectedLocation,
    selectedDateRange,
    setSelectedDateRange,
    selectedCrime,
    selectedSource,
    onLocationSelect,
    locationData.info,
    locationData.dateRange,
    locationData.source,
    locationTypeData.dateRange,
    locationTypeData.crime,
    locationTypeData.data,
    fetchData,
    setDisplayedLocation,
    setCityResolutionData,
    setShowCityResolutionModal,
    setAvailableYears,
    yearCheckInProgress,
    compareMode,
    onCompareModeChange
  ]);

  const findCachedData = (name, type, data) => {
    if (!data) return null;

    if (data.features) {
      const feature = data.features.find(f => f.properties.name === name);
      if (feature) return feature.properties;
    }

    if (data.locations) {
      const location = data.locations.find(l => l.name === name);
      if (location) return location;
    }

    if ((type === 'county' || type === 'city') && name.includes(',')) {
      const [locationName, state] = name.split(',').map(part => part.trim());
      
      let cleanLocationName;
      let locationField;
      
      if (type === 'county') {
        cleanLocationName = locationName.replace(/ County$/, '').replace(/ Parish$/, '').replace(/ Borough$/, '').toLowerCase();
        locationField = 'county';
      } else if (type === 'city') {
        cleanLocationName = locationName.toLowerCase();
        locationField = 'city';
      }
      
      const stateLower = state.toLowerCase();

      if (data.features) {
        const locationFeature = data.features.find(f =>
          f.properties[locationField]?.toLowerCase() === cleanLocationName &&
          f.properties.state?.toLowerCase() === stateLower
        );
        if (locationFeature) return locationFeature.properties;
      }

      if (data.locations) {
        const locationMatch = data.locations.find(l =>
          l[locationField]?.toLowerCase() === cleanLocationName &&
          l.state?.toLowerCase() === stateLower
        );
        if (locationMatch) return locationMatch;
      }
    }

    return null;
  };

  // 2. Simplified loading state management
  useEffect(() => {
    const isLoading = locationTypeData.loading || locationData.loading;
    const isHistoricalDataLoading =
      isHistorical &&
      (!hasResolvedHistoricalFetch || isHistoricalLoading);
    
    if (isHistoricalDataLoading) {
      setShowLoading(true);
    } else if (!isLoading) {
      setShowLoading(false);
    }
  }, [
    locationTypeData.loading,
    locationData.loading,
    isHistorical,
    isHistoricalLoading,
    hasResolvedHistoricalFetch,
  ]);

  // 3. Modified location display management - CRITICAL for transitions
  useEffect(() => {
    if (!selectedLocation?.info?.name) return;
    
    if (locationData.loading || locationTypeData.loading) return;

    if (selectedLocation?.info?.type === 'city' && 
        selectedLocation?.info?.name?.includes(',') &&
        !selectedLocation?.info?.wasResolved &&
        !selectedLocation?.info?.skipResolutionCheck &&
        locationTypeData.data?.locations) {
      
      const [searchCity, searchState] = selectedLocation.info.name.split(',').map(s => s.trim());
      
      const isConstituent = locationTypeData.data.locations.some(loc => {
        if (loc.state?.toLowerCase() !== searchState.toLowerCase()) return false;
        
        if (loc.jurisdictions) {
          const jurisdictionCities = loc.jurisdictions.split(',').map(c => c.trim().toLowerCase());
          if (jurisdictionCities.includes(searchCity.toLowerCase())) {
            return true;
          }
        }
        
        if (loc.city?.includes('/')) {
          const cityParts = loc.city.split('/').map(c => c.trim().toLowerCase());
          if (cityParts.includes(searchCity.toLowerCase())) {
            return true;
          }
        }
        
        return false;
      });
      
      if (isConstituent) {
        return;
      }
    }
    
    if (selectedLocation.info.name.startsWith('All ') && locationTypeData.data?.aggregateStats) {
      setDisplayedLocation({
        ...selectedLocation,
        data: { current: locationTypeData.data.aggregateStats }
      });
      return;
    }

    if (!selectedLocation.info.name.startsWith('All ') && locationTypeData.data?.features) {
      const cachedData = locationTypeData.data.features.find(
        feature => feature.properties.name === selectedLocation.info.name
      );
      
      if (cachedData) {
        setDisplayedLocation({
          ...selectedLocation,
          data: { current: cachedData.properties }
        });
        return;
      }
    }
    
    if (locationData.data) {
      setDisplayedLocation({
        ...selectedLocation,
        data: locationData.data
      });
      return;
    }
    
    setDisplayedLocation(selectedLocation);
  }, [selectedLocation, setDisplayedLocation, locationTypeData.data, locationData.data, locationData.loading, locationTypeData.loading]);

  // 4. Simplified view and location type management
  useEffect(() => {
    if (selectedLocation?.info?.name) {
      if (selectedLocation.info.name.startsWith('All ')) {
        setLocationType(selectedLocation.info.type);
        setView('locationType');
      } else {
        const prevState = extractState(locationData.info);
        const newState = extractState(selectedLocation.info);
        const isFromStateSpecificAll = prevState && locationData.info?.name?.startsWith('All ');
        
        if (isFromStateSpecificAll && prevState === newState) {
          if (locationData.info?.name === selectedLocation.info.name && locationData.data?.current) {
            setLocationType(null);
            setView('locationName');
          }
        } else {
          setLocationType(null);
          setView('locationName');
        }
      }
    }
  }, [
    selectedLocation.info,
    selectedLocation?.info?.name, 
    selectedLocation?.info?.type, 
    locationData.data,
    locationData.info,
    locationData.info?.name
  ]);

  // 5. Table management - pagination and sorting
  useEffect(() => {
    if (locationTypeData?.data?.locations) {
      setTotalItems(locationTypeData.data.locations.length);
    }
  }, [locationTypeData?.data]);

  // 6. Historical data management
  useEffect(() => {
    if (!isHistorical || !displayedLocation?.info?.name) return;
    if (historicalData && historicalDataSource === selectedSource) {
      setHasResolvedHistoricalFetch(true);
      return;
    }

    const fetchHistorical = async () => {
      setIsHistoricalLoading(true);
      setHasResolvedHistoricalFetch(false);
      try {
        const result = await fetchLocationNameHistoricalData(
          displayedLocation.info.name,
          displayedLocation.info.type,
          selectedSource
        );
        
        if (result.historical) {
          setHistoricalData(result.historical);
          setHistoricalDataSource(selectedSource);
        }
      } catch (error) {
        console.error('Failed to fetch historical data:', error);
      } finally {
        setIsHistoricalLoading(false);
        setHasResolvedHistoricalFetch(true);
      }
    };

    fetchHistorical();
  }, [
    isHistorical, 
    displayedLocation?.info?.name, 
    displayedLocation?.info?.type, 
    selectedSource, 
    historicalData,
    historicalDataSource
  ]);

  // 7a. Reset cached historical series when location/source changes (not the historical mode —
  // that comes from the URL / parent; clearing it here broke back/forward when ?historical=yes).
  useEffect(() => {
    setHistoricalData(null);
    setHistoricalDataSource(selectedSource);
    setHasResolvedHistoricalFetch(false);
  }, [displayedLocation?.info?.name, selectedSource]);

  // 7b. User-driven location changes should always return to current data. POP restores
  // historical state from the URL with `loading: false`, so only reset when the location key
  // actually changes during a loading transition.
  useEffect(() => {
    const locationKey = selectedLocation?.info?.name && selectedLocation?.info?.type
      ? `${selectedLocation.info.type}::${selectedLocation.info.name}`
      : null;

    if (lastLocationKeyRef.current == null) {
      lastLocationKeyRef.current = locationKey;
      return;
    }

    const hasLocationChanged =
      locationKey != null && locationKey !== lastLocationKeyRef.current;

    if (isHistorical && selectedLocation?.loading && hasLocationChanged) {
      setIsHistorical(false);
    }

    lastLocationKeyRef.current = locationKey;
  }, [
    isHistorical,
    selectedLocation?.loading,
    selectedLocation?.info?.name,
    selectedLocation?.info?.type,
    setIsHistorical,
  ]);

  // 8. Reset scroll position when sort changes
  useEffect(() => {
    if (view === 'locationType' && sortConfig) {
      const scrollContainer = document.querySelector('.main-table-scroll-container');
      if (scrollContainer) {
        scrollContainer.scrollTop = 0;
      }
    }
  }, [sortConfig, view]);

  // 9. Reset population filter when location changes
  useEffect(() => {
    if (popGroupSetByRouterRef?.current) {
      pendingPopulationReset.current = false;
      popGroupSetByRouterRef.current = false;
      return;
    }

    const isNationalAll = selectedLocation?.info?.name === 'All Counties' || selectedLocation?.info?.name === 'All Cities';
    const isStateAll = selectedLocation?.info?.name?.startsWith('All ') && !isNationalAll;
    
    if (isNationalAll || (isStateAll && view !== 'locationType')) {
      setPopulationGroup('all');
      pendingPopulationReset.current = false;
    } else if (selectedLocation?.info?.name) {
      pendingPopulationReset.current = true;
    }
  }, [selectedLocation?.info?.name, view, setPopulationGroup, popGroupSetByRouterRef]);

  // 10. Separate population effect to handle the delayed reset when data loads
  useEffect(() => {
    if (popGroupSetByRouterRef?.current) {
      pendingPopulationReset.current = false;
      popGroupSetByRouterRef.current = false;
      return;
    }

    if (pendingPopulationReset.current && locationTypeData.data && !locationTypeData.loading) {
      setPopulationGroup('all');
      pendingPopulationReset.current = false;
    }
  }, [locationTypeData.data, locationTypeData.loading, setPopulationGroup, popGroupSetByRouterRef]);

  const handleLocationTypeSelect = ({ type, state }) => {
    let displayName;
    
    if (type === 'county') {
      if (state) {
        const stateAbbr = getStateAbbreviation(state);
        displayName = `All ${stateAbbr} Counties`;
      } else {
        displayName = 'All Counties';
      }
    } else if (type === 'city') {
      if (state) {
        const stateAbbr = getStateAbbreviation(state);
        displayName = `All ${stateAbbr} Cities`;
      } else {
        displayName = 'All Cities';
      }
    } else {
      displayName = `All ${type.charAt(0).toUpperCase() + type.slice(1)}s`;
    }
    
    setCurrentPage(1);
    
    const locationObject = {
      name: displayName,
      type: type,
      info: {
        name: displayName,
        type: type,
        state: state,
        stateAbbr: state ? getStateAbbreviation(state) : null
      }
    };
    
    onLocationSelect(locationObject);
  };

  const locationToRender = useMemo(() => {
    if (isHistorical && historicalData) {
      return {
        ...locationData,
        data: {
          ...locationData.data,
          historical: historicalData,
        },
      };
    }
    return locationData;
  }, [isHistorical, historicalData, locationData]);

  const locationNameForDisplay = useMemo(() => {
    return displayedLocation?.info?.name || selectedLocation?.info?.name || '';
  }, [displayedLocation?.info?.name, selectedLocation?.info?.name]);

  const locationTypeForDisplay = useMemo(() => {
    return displayedLocation?.info?.type || selectedLocation?.info?.type || null;
  }, [displayedLocation?.info?.type, selectedLocation?.info?.type]);

  const exportContext = useMemo(() => ({
    view,
    activeTab,
    isHistorical: activeTab === 'reports' ? false : isHistorical,
    selectedView,
    locationData: locationToRender,
    locationTypeData,
    selectedCrime,
    selectedDateRange,
    selectedSource,
    compareMode: getActiveCompareMode(),
    rateViewMode,
    currentPage,
    itemsPerPage,
    sortConfig,
    locationName: locationNameForDisplay,
    locationType: locationTypeForDisplay,
    populationGroup,
    contentRef,
    captureRef: captureRef || contentRef,
  }), [
    view, activeTab, isHistorical, selectedView,
    locationToRender, locationTypeData,
    selectedCrime, selectedDateRange, selectedSource,
    rateViewMode, currentPage, itemsPerPage,
    sortConfig, locationNameForDisplay, locationTypeForDisplay, populationGroup, getActiveCompareMode, captureRef,
  ]);

  useEffect(() => {
    onExportContextChange?.(exportContext);
  }, [exportContext, onExportContextChange]);

  const getCrimeAggregateStats = useCallback(() => {
    if (!locationTypeData?.data?.crimeAggregates) {
      return locationTypeData?.data?.aggregateStats;
    }
    
    return locationTypeData.data.crimeAggregates[selectedCrime] || locationTypeData.data.aggregateStats;
  }, [locationTypeData?.data, selectedCrime]);

  const hasPreviousData = () => {
    if (activeTab !== 'trends') return true;
    
    if (view === 'locationType') {
      return locationTypeData?.data?.locations?.some(location => {
        const prevField = `${selectedCrime.toLowerCase()}_count_prev`;
        return location[prevField] !== null && 
               location[prevField] !== undefined &&
               location[prevField] !== 'N/A';
      });
    } else {
      const prevField = `${selectedCrime.toLowerCase()}_count_prev`;
      return locationData?.data?.current?.[prevField] !== null && 
             locationData?.data?.current?.[prevField] !== undefined &&
             locationData?.data?.current?.[prevField] !== 'N/A';
    }
  };

  const locationTypeDataIsNational = useMemo(() => {
    const rows = locationTypeData?.data?.locations;
    if (!rows?.length) return false;

    const firstState = rows[0]?.state;
    return rows.some(location => location.state !== firstState);
  }, [locationTypeData?.data?.locations]);

  const shouldUseNationalAggregateMode =
    view === 'locationType' &&
    (selectedLocation?.info?.type === 'city' || selectedLocation?.info?.type === 'county') &&
    Boolean(selectedLocation?.info?.state) &&
    locationTypeDataIsNational;

  const effectiveLocationTypeCompareMode = shouldUseNationalAggregateMode
    ? 'national'
    : compareMode;

  const effectiveLocationTypeRateViewMode = shouldUseNationalAggregateMode &&
    rateViewMode?.rateScope === 'state'
      ? { ...rateViewMode, rateScope: 'national' }
      : rateViewMode;

  const effectiveHeaderCompareMode =
    view === 'locationType'
      ? activeTab === 'rates'
        ? effectiveLocationTypeRateViewMode.rateScope
        : effectiveLocationTypeCompareMode
      : getActiveCompareMode();

  const shouldForceHistoricalLoadingOverlay =
    isHistorical && (!hasResolvedHistoricalFetch || isHistoricalLoading);

  const shouldSuppressAggregateLoadingOverlay =
    shouldUseNationalAggregateMode &&
    Boolean(locationTypeData.data?.locations?.length) &&
    !shouldForceHistoricalLoadingOverlay;

  const shouldShowLoadingOverlay =
    shouldForceHistoricalLoadingOverlay ||
    (!shouldSuppressAggregateLoadingOverlay &&
      ((showLoading && !isStateTransition) ||
        (isFetching.current && !isStateTransition) ||
        (selectedLocation.loading &&
          selectedLocation?.info?.type !== displayedLocation?.info?.type &&
          !isStateTransition)));

  return (
    <div className="data-view-container">
      {isMobile && (
        <div className="filters-container">
          <Filters 
            isMobileLayout={isMobile}
            isPinned={isPinned}
            setIsPinned={setIsPinned}
            selectedLocation={selectedLocation}
            onLocationSelect={onLocationSelect}
            selectedCrime={selectedCrime}
            setSelectedCrime={setSelectedCrime}
            selectedDateRange={selectedDateRange}
            setSelectedDateRange={setSelectedDateRange}
            selectedSource={selectedSource}
            setSelectedSource={setSelectedSource}
            availableYears={availableYears}
            onSaveSearch={onSaveSearch}
            userPlan={userPlan}
            mostRecentYear={mostRecentYear}
          />
        </div>
      )}

      {!isMobile && selectedLocation?.info?.name && view === 'locationName' &&
        !(activeTab === 'scores' && isNation) && activeTab !== 'reports' && 
        locationData.data &&
        <div className="view-toggle">
          <button
            className={`toggle-button ${selectedView === 'table' ? 'active' : ''}`}
            onClick={() => setSelectedView('table')}
          >
            <TableIcon size={20} />
            <span>Data</span>
          </button>
          <button
            className={`toggle-button ${selectedView === 'chart' ? 'active' : ''}`}
            onClick={() => setSelectedView('chart')}
          >
            <ChartNoAxesCombined size={20} />
            <span>Charts</span>
          </button>
        </div>
      }     

      {shouldShowLoadingOverlay ? (
        <LoadingOverlay />
      ) : ( 
        <>
          {((view === 'locationType' && 
            locationTypeData.data && 
            locationTypeData.data.locations?.length && 
            (hasPreviousData() || activeTab !== 'trends')
          ) ||
          (view === 'locationName' && 
            locationData.data &&
            displayedLocation?.info?.name && 
            !(activeTab === 'scores' && isNation)
          )) && (
            <TableHeader 
              activeTab={activeTab}
              selectedView={selectedView}
              setActiveTab={setActiveTab}
              isHistorical={isHistorical}
              setIsHistorical={setIsHistorical}
              selectedData={selectedData}
              setSelectedData={setSelectedData}
              sortConfig={sortConfig}
              onSortChange={onSortChange}
              selectedLocation={displayedLocation}
              locationType={displayedLocation?.info?.type || locationType}
              selectedCrime={selectedCrime}
              onMapRangeChange={onMapRangeChange}
              compareMode={effectiveHeaderCompareMode}
              onCompareModeChange={onCompareModeChange}
              rateViewMode={view === 'locationType' ? effectiveLocationTypeRateViewMode : rateViewMode}
              setRateViewMode={setRateViewMode}
              view={view}
              showSortOptions={view === 'locationType' && activeTab !== 'reports'}
              showHistoricalToggle={view === 'locationName'}
              showDataSelector={view === 'locationName' && activeTab === 'reports'}
              showExportActions={true}
              userPlan={userPlan}
              onRequestUpgrade={onRequestUpgrade}
              exportContext={exportContext}
            />
          )}

          {view === 'locationType' && (
            <div className="data-view-content" ref={contentRef}>
              {!locationTypeData.data || !locationTypeData.data.locations?.length ? (
                <div className="loading">No Data Available</div>
              ) : !hasPreviousData() && activeTab === 'trends' ? (
                <div className="loading">No Previous Data Available</div>
              ) : (
                <>
                  {activeTab === 'reports' ? (
                    <div className="data-view-scroll-area">
                      <div className="location-type-reports-view">
                        <ReportCards 
                          setActiveTab={setActiveTab}
                          view={view}
                          selectedLocation={{
                            info: view === 'locationType' && (locationType === 'county' || locationType === 'city') 
                              ? (() => {
                                  const isNationalData = locationTypeData.data?.locations?.length > 1 && 
                                    locationTypeData.data.locations.some(loc => loc.state !== locationTypeData.data.locations[0].state);
                                  const stateValue = isNationalData ? null : locationTypeData.data?.locations?.[0]?.state;
                                  
                                  return {
                                    name: locationTypeData.type === 'county' 
                                      ? (stateValue ? `All ${getStateAbbreviation(stateValue)} Counties` : 'All Counties')
                                      : (stateValue ? `All ${getStateAbbreviation(stateValue)} Cities` : 'All Cities'),
                                    type: locationTypeData.type,
                                    state: stateValue,
                                    stateAbbr: stateValue ? getStateAbbreviation(stateValue) : null,
                                    total_counties_state: locationTypeData.data?.locations?.[0]?.total_counties_state,
                                    total_cities_state: locationTypeData.data?.locations?.[0]?.total_cities_state
                                  };
                                })()
                              : displayedLocation.info,
                            data: {
                              current: (() => {
                                const isAllLocation = displayedLocation?.info?.name?.startsWith('All ');
                                
                                if (isAllLocation) {
                                  return locationTypeData?.data?.aggregateStats || {};
                                }
                                
                                if (locationData?.data?.current) {
                                  const hasCrimeData = [
                                    'all_crime_count', 'violent_crime_count', 'property_crime_count',
                                    'murder_count', 'rape_count', 'robbery_count', 'aggravated_assault_count',
                                    'burglary_count', 'larceny_theft_count', 'motor_vehicle_theft_count'
                                  ].some(field => 
                                    locationData.data.current[field] !== null && 
                                    locationData.data.current[field] !== undefined && 
                                    locationData.data.current[field] !== 'N/A'
                                  );
                                  
                                  if (hasCrimeData) {
                                    return locationData.data.current;
                                  }
                                }
                                
                                if (locationTypeData?.data?.features) {
                                  const cached = locationTypeData.data.features.find(
                                    f => f.properties.name === displayedLocation?.info?.name
                                  )?.properties;
                                  if (cached) return cached;
                                }
                                
                                return {};
                              })()
                            },
                            loading: locationData.loading || locationTypeData.loading
                          }}
                          selectedCrime={selectedCrime}
                          aggregateStats={getCrimeAggregateStats()}
                          compareMode={effectiveLocationTypeCompareMode}           
                          rateViewMode={effectiveLocationTypeRateViewMode}        
                          onCompareModeChange={onCompareModeChange}  
                          onRateViewModeChange={setRateViewMode}
                        />
                      </div>
                    </div>
                  ) : (
                    <>                  
                      {activeTab === 'totals' && (
                        <LocationTypeTotalsTable
                          data={locationTypeData.data}
                          locationType={locationType}
                          onLocationSelect={onLocationSelect}
                          selectedLocation={displayedLocation}
                          selectedCrime={selectedCrime}
                          selectedDateRange={selectedDateRange}
                          selectedSource={selectedSource}
                          sortConfig={sortConfig}
                          onSortChange={onSortChange}
                          currentPage={currentPage}
                          itemsPerPage={itemsPerPage}
                          onMapRangeChange={onMapRangeChange}
                          compareMode={effectiveLocationTypeCompareMode}
                          onCompareModeChange={onCompareModeChange}
                          hoveredLocation={hoveredLocation}
                          setHoveredLocation={setHoveredLocation}
                        />
                      )}
                      {activeTab === 'trends' && (
                        <LocationTypeTrendsTable
                          data={locationTypeData.data}
                          locationType={locationType}
                          onLocationSelect={onLocationSelect}
                          selectedLocation={displayedLocation}
                          selectedCrime={selectedCrime}
                          selectedDateRange={selectedDateRange}
                          selectedSource={selectedSource}
                          sortConfig={sortConfig}
                          onSortChange={onSortChange}
                          currentPage={currentPage}
                          itemsPerPage={itemsPerPage}
                          onMapRangeChange={onMapRangeChange}
                          compareMode={effectiveLocationTypeCompareMode}
                          onCompareModeChange={onCompareModeChange}
                          hoveredLocation={hoveredLocation}
                          setHoveredLocation={setHoveredLocation}
                        />
                      )}
                      {activeTab === 'rates' && (
                        <LocationTypeRatesTable
                          data={locationTypeData.data}
                          locationType={locationType}
                          onLocationSelect={onLocationSelect}
                          selectedLocation={displayedLocation}
                          selectedCrime={selectedCrime}
                          selectedDateRange={selectedDateRange}
                          selectedSource={selectedSource}
                          sortConfig={sortConfig}
                          onSortChange={onSortChange}
                          currentPage={currentPage}
                          itemsPerPage={itemsPerPage}
                          onMapRangeChange={onMapRangeChange}
                          compareMode={effectiveLocationTypeCompareMode}
                          onCompareModeChange={onCompareModeChange}
                          rateViewMode={effectiveLocationTypeRateViewMode}
                          onRateViewModeChange={setRateViewMode}
                          hoveredLocation={hoveredLocation}
                          setHoveredLocation={setHoveredLocation}
                          onFilteredDataChange={setRatesTableFilter}
                          populationGroup={populationGroup}
                          onPopulationGroupChange={setPopulationGroup}
                        />
                      )}
                      {activeTab === 'scores' && (
                        <LocationTypeScoresTable
                          data={locationTypeData.data}
                          locationType={locationType}
                          onLocationSelect={onLocationSelect}
                          selectedLocation={displayedLocation}
                          selectedCrime={selectedCrime}
                          selectedDateRange={selectedDateRange}
                          selectedSource={selectedSource}
                          sortConfig={sortConfig}
                          onSortChange={onSortChange}
                          currentPage={currentPage}
                          itemsPerPage={itemsPerPage}
                          onMapRangeChange={onMapRangeChange}
                          compareMode={effectiveLocationTypeCompareMode}
                          onCompareModeChange={onCompareModeChange}
                          hoveredLocation={hoveredLocation}
                          setHoveredLocation={setHoveredLocation}
                        />
                      )}

                      <Pagination 
                        currentPage={currentPage}
                        totalPages={activeTab === 'rates' && ratesTableFilter ? 
                          Math.ceil(ratesTableFilter.totalItems / itemsPerPage) : 
                          totalPages}
                        itemsPerPage={itemsPerPage}
                        setItemsPerPage={setItemsPerPage}
                        setCurrentPage={setCurrentPage}
                        startItem={activeTab === 'rates' && ratesTableFilter ? 
                          ratesTableFilter.startItem : 
                          startItem}
                        endItem={activeTab === 'rates' && ratesTableFilter ? 
                          ratesTableFilter.endItem : 
                          endItem}
                        totalItems={activeTab === 'rates' && ratesTableFilter ? 
                          ratesTableFilter.totalItems : 
                          totalItems}
                      />
                    </>
                  )}
                  
                  {((locationType === 'county' && displayedLocation?.info?.name !== 'All Counties') ||
                    (locationType === 'city' && displayedLocation?.info?.name !== 'All Cities')) && 
                   activeTab === 'reports' && 
                    <Breadcrumb 
                      selectedLocation={displayedLocation}
                      onTypeClick={handleLocationTypeSelect}
                      selectedView={selectedView}
                      isHistorical={isHistorical}
                      activeTab={activeTab}
                    />
                  }
                </>
              )}
            </div>
          )}

          {view === 'locationName' && !displayedLocation?.info?.name?.startsWith('All ') && (
            <div className="data-view-content" ref={contentRef}>
              {!displayedLocation?.info?.name ? (
                <div className="loading">Please Select Location</div>
              ) : activeTab === 'scores' && isNation ? (
                <div className="loading">Scores are not available for the entire nation</div>
              ) : (
                <>
                  <div className="data-view-scroll-area">
                    {activeTab === 'reports' ? (
                      <ReportCards 
                        activeTab={activeTab} 
                        setActiveTab={setActiveTab}
                        selectedLocation={{
                          ...displayedLocation,
                          data: {
                            current: locationData?.data?.current || locationTypeData?.data?.aggregateStats
                          },
                          loading: locationData.loading || locationTypeData.loading
                        }}
                        selectedCrime={selectedCrime}
                        compareMode={compareMode}           
                        rateViewMode={rateViewMode}        
                        onCompareModeChange={onCompareModeChange} 
                        onRateViewModeChange={setRateViewMode}
                      />
                    ) : (
                      <>
                        {selectedView === 'table' && (
                          <>
                            {activeTab === 'totals' && (
                              isHistorical ? (
                                <HistoricalTotalsTable 
                                  selectedLocation={locationToRender}
                                  selectedCrime={selectedCrime}
                                  selectedDateRange={selectedDateRange}
                                  setSelectedDateRange={setSelectedDateRange}
                                  setIsHistorical={setIsHistorical}
                                  selectedSource={selectedSource}
                                  compareMode={compareMode}
                                  onCompareModeChange={onCompareModeChange}
                                />
                              ) : (
                                <LocationNameTotalsTable 
                                  selectedLocation={locationData}
                                  selectedCrime={selectedCrime}
                                  selectedSource={selectedSource}
                                  compareMode={compareMode}
                                  onCompareModeChange={onCompareModeChange}
                                />
                              )
                            )}
                            {activeTab === 'trends' && (
                              isHistorical ? (
                                <HistoricalTrendsTable 
                                  selectedLocation={locationToRender}
                                  selectedCrime={selectedCrime}
                                  selectedDateRange={selectedDateRange}
                                  setSelectedDateRange={setSelectedDateRange}
                                  setIsHistorical={setIsHistorical}
                                  selectedSource={selectedSource}
                                  compareMode={compareMode}
                                  onCompareModeChange={onCompareModeChange}
                                />
                              ) : (
                                <LocationNameTrendsTable 
                                  selectedLocation={locationData}
                                  selectedCrime={selectedCrime}
                                  selectedDateRange={selectedDateRange}
                                  selectedSource={selectedSource}
                                  compareMode={compareMode}
                                  onCompareModeChange={onCompareModeChange}
                                />
                              )
                            )}
                            {activeTab === 'rates' && (
                              isHistorical ? (
                                <HistoricalRatesTable 
                                  selectedLocation={locationToRender}
                                  selectedCrime={selectedCrime}
                                  selectedDateRange={selectedDateRange}
                                  setSelectedDateRange={setSelectedDateRange}
                                  setIsHistorical={setIsHistorical}
                                  selectedSource={selectedSource}
                                  compareMode={rateViewMode}
                                  onCompareModeChange={setRateViewMode}
                                  onUnifiedCompareModeChange={onCompareModeChange}
                                />
                              ) : (
                                <LocationNameRatesTable 
                                  selectedLocation={locationData}
                                  selectedCrime={selectedCrime}
                                  selectedSource={selectedSource}
                                  compareMode={rateViewMode}
                                  onCompareModeChange={setRateViewMode}
                                  onUnifiedCompareModeChange={onCompareModeChange}
                                />
                              )
                            )}
                            {activeTab === 'scores' && (
                              isHistorical ? (
                                <HistoricalScoresTable 
                                  selectedLocation={locationToRender}
                                  selectedCrime={selectedCrime}
                                  selectedDateRange={selectedDateRange}
                                  setSelectedDateRange={setSelectedDateRange}
                                  setIsHistorical={setIsHistorical}
                                  compareMode={compareMode}
                                  onCompareModeChange={onCompareModeChange}
                                />
                              ) : (
                                <LocationNameScoresTable 
                                  selectedLocation={locationData}
                                  selectedCrime={selectedCrime}
                                  compareMode={compareMode}
                                  onCompareModeChange={onCompareModeChange}
                                />
                              )
                            )}
                          </>
                        )}
                        {selectedView === 'chart' && (
                          <>
                            {activeTab === 'totals' && (
                              isHistorical ? (
                                <HistoricalTotalsChart 
                                  selectedLocation={locationToRender}
                                  selectedCrime={selectedCrime}
                                  selectedChartOption={selectedChartOption}
                                  onChartOptionChange={onChartOptionChange}
                                  selectedSource={selectedSource}
                                  compareMode={compareMode}
                                  onCompareModeChange={onCompareModeChange}
                                />
                              ) : (
                                <LocationNameTotalsChart 
                                  selectedLocation={locationData}
                                  selectedCrime={selectedCrime}
                                  selectedDateRange={selectedDateRange}
                                  selectedChartOption={selectedChartOption}
                                  onChartOptionChange={onChartOptionChange}
                                  selectedSource={selectedSource}
                                  compareMode={compareMode}
                                  onCompareModeChange={onCompareModeChange}
                                />
                              )
                            )}
                            {activeTab === 'trends' && (
                              isHistorical ? (
                                <HistoricalTrendsChart 
                                  selectedLocation={locationToRender}
                                  selectedCrime={selectedCrime}
                                  selectedChartOption={selectedChartOption}
                                  onChartOptionChange={onChartOptionChange}
                                  selectedSource={selectedSource}
                                  compareMode={compareMode}
                                  onCompareModeChange={onCompareModeChange}
                                />
                              ) : (
                                <LocationNameTrendsChart 
                                  selectedLocation={locationData}
                                  selectedCrime={selectedCrime}
                                  selectedDateRange={selectedDateRange}
                                  selectedChartOption={selectedChartOption}
                                  onChartOptionChange={onChartOptionChange}
                                  selectedSource={selectedSource}
                                  compareMode={compareMode}
                                  onCompareModeChange={onCompareModeChange}
                                />
                              )
                            )}
                            {activeTab === 'rates' && (
                              isHistorical ? (
                                <HistoricalRatesChart 
                                  selectedLocation={locationToRender}
                                  selectedCrime={selectedCrime}
                                  selectedChartOption={selectedChartOption}
                                  onChartOptionChange={onChartOptionChange}
                                  selectedSource={selectedSource}
                                  compareMode={rateViewMode}
                                  onCompareModeChange={setRateViewMode}
                                  onUnifiedCompareModeChange={onCompareModeChange}
                                />
                              ) : (
                                <LocationNameRatesChart 
                                  selectedLocation={locationData}
                                  selectedCrime={selectedCrime}
                                  selectedDateRange={selectedDateRange}
                                  selectedChartOption={selectedChartOption}
                                  onChartOptionChange={onChartOptionChange}
                                  selectedSource={selectedSource}
                                  compareMode={rateViewMode}
                                  onCompareModeChange={setRateViewMode}
                                  onUnifiedCompareModeChange={onCompareModeChange}
                                />
                              )
                            )}
                            {activeTab === 'scores' && (
                              isHistorical ? (
                                <HistoricalScoresChart 
                                  selectedLocation={locationToRender}
                                  selectedCrime={selectedCrime}
                                  selectedChartOption={selectedChartOption}
                                  onChartOptionChange={onChartOptionChange}
                                  selectedSource={selectedSource}
                                  compareMode={compareMode}
                                  onCompareModeChange={onCompareModeChange}
                                />
                              ) : (
                                <LocationNameScoresChart 
                                  selectedLocation={locationData}
                                  selectedCrime={selectedCrime}
                                  selectedDateRange={selectedDateRange}
                                  selectedChartOption={selectedChartOption}
                                  onChartOptionChange={onChartOptionChange}
                                  selectedSource={selectedSource}
                                  compareMode={compareMode}
                                  onCompareModeChange={onCompareModeChange}
                                />
                              )
                            )}
                          </>
                        )}
                      </>
                    )}
                  </div>
                  {!isNation && !displayedLocation?.info?.name?.startsWith('All ') && (
                    <Breadcrumb 
                      selectedLocation={displayedLocation}
                      onTypeClick={handleLocationTypeSelect}
                      selectedView={selectedView}
                      isHistorical={isHistorical}
                      activeTab={activeTab}
                    />
                  )}
                </>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default DataView;